import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  MDBBtn,
  MDBContainer,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
} from "mdb-react-ui-kit";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import config from "../config";
import { usePageState } from "../context/AppStateContext";
import "./SorterPage.css";
import AutoFillLoader from "../components/Common/Loaders/AutoFillLoader";
import AddProduct from "../components/AddProduct/AddProduct";
import { useMessageBox } from "../components/Common/MessageBox";
import {
  DEMO_PROGRESS_STORAGE_KEY,
} from "../utils/onboardingJourney";
import {
  buildDemoSessionFromCheckout,
  createDefaultDemoSession,
  DEMO_SESSION_KEY,
  loadDemoSession,
  markDemoFeatureCompleted,
} from "../utils/demoSession";

const DEMO_INTEGRATION_MODAL_DISMISS_KEY = "demo_integration_modal_dismissed";

const DEMO_CARD_ORDER = ["smartlist", "smartscan", "pos"];

const FEATURE_META = {
  smartscan: {
    title: "SmartScan",
    description: "Scan and enrich your product data quickly before listing.",
    badge: "AI Powered",
  },
  smartlist: {
    title: "SmartList",
    description: "Generate and publish optimized listings across channels.",
    badge: "Cross Listing",
  },
  pos: {
    title: "POS",
    description: "Run in-store checkout and sync inventory in real time.",
    badge: "Retail Ready",
  },
};

const normalizeFeature = (value = "") =>
  value.toString().trim().toLowerCase().replace(/\s+/g, "");

/** When DB stores plan text but not selected_features, recover addon ids from the label. */
function segmentToFeatureId(segment) {
  const s = normalizeFeature(segment);
  if (!s) return null;
  if (s.includes("smartscan")) return "smartscan";
  if (s.includes("smartlist")) return "smartlist";
  if (s === "pos" || s.includes("pointofsale")) return "pos";
  return null;
}

function parseFeaturesFromPlan(plan) {
  if (!plan || typeof plan !== "string") return [];
  const p = plan.trim();
  if (/\bunlimited\b/i.test(p)) {
    return ["smartscan", "smartlist", "pos"];
  }
  const paren = p.match(/\(([^)]+)\)/);
  const segmentSource = paren ? paren[1] : p;
  const parts = segmentSource.split(",").map((x) => x.trim()).filter(Boolean);
  const ids = [];
  for (const part of parts) {
    const id = segmentToFeatureId(part);
    if (id && FEATURE_META[id]) ids.push(id);
  }
  if (!ids.length && /customize/i.test(p)) {
    for (const id of Object.keys(FEATURE_META)) {
      if (new RegExp(`\\b${id}\\b`, "i").test(p)) ids.push(id);
    }
  }
  return [...new Set(ids)];
}

function resolveFeatureList({ apiFeatures, localAddons, planFromApi, planFromLocal }) {
  const fromApi = Array.isArray(apiFeatures) ? apiFeatures.filter(Boolean) : [];
  if (fromApi.length) return fromApi;
  const fromLocal = Array.isArray(localAddons) ? localAddons.filter(Boolean) : [];
  if (fromLocal.length) return fromLocal;
  const fromPlanApi = parseFeaturesFromPlan(planFromApi);
  if (fromPlanApi.length) return fromPlanApi;
  return parseFeaturesFromPlan(planFromLocal);
}

const API_BASE = config.API_BASE_URL;

/**
 * Smart Scan UI aligned with SorterPage: same analyze API and table UX.
 * Demo rules: no smartscan list/save DB calls; session state uses demo_* localStorage keys.
 */
function DemoSmartScanPane({ onBack, onComplete }) {
  const [selectedImages, setSelectedImages] = useState([]);
  const [results, setResults] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const fileInputRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();
  const { setState: setAddProductState } = usePageState("addProduct");

  const [isProcessing, setIsProcessing] = useState(false);
  const [autoFillMessageIndex, setAutoFillMessageIndex] = useState(0);
  const completedRef = useRef(false);

  const autoFillMessages = [
    "Analyze image",
    "Get the product details",
    "Authentication",
    "Condition",
    "Generating description",
  ];

  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(() => {
      setAutoFillMessageIndex((prev) => (prev + 1) % autoFillMessages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isProcessing, autoFillMessages.length]);

  const safeSetItem = (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      if (error.name === "QuotaExceededError") {
        try {
          localStorage.removeItem("demo_sorter_selectedImages");
          localStorage.removeItem("demo_sorter_results");
          localStorage.setItem(key, value);
          return true;
        } catch (retryError) {
          console.error("localStorage still full after cleanup:", retryError);
          return false;
        }
      }
      console.error("Error saving to localStorage:", error);
      return false;
    }
  };

  useEffect(() => {
    try {
      const savedImages = localStorage.getItem("demo_sorter_selectedImages");
      const savedResults = localStorage.getItem("demo_sorter_results");
      if (savedImages) setSelectedImages(JSON.parse(savedImages));
      if (savedResults) setResults(JSON.parse(savedResults));
    } catch (error) {
      console.error("Error loading saved demo sorter data:", error);
      localStorage.removeItem("demo_sorter_results");
      localStorage.removeItem("demo_sorter_selectedImages");
    }
  }, []);

  useEffect(() => {
    if (selectedImages.length > 0) {
      try {
        safeSetItem("demo_sorter_selectedImages", JSON.stringify(selectedImages));
      } catch (error) {
        console.error("Error saving images:", error);
      }
    } else {
      try {
        localStorage.removeItem("demo_sorter_selectedImages");
      } catch (error) {
        console.error("Error removing data:", error);
      }
    }
  }, [selectedImages]);

  useEffect(() => {
    if (results.length === 0) {
      try {
        localStorage.removeItem("demo_sorter_results");
      } catch (e) {
        /* ignore */
      }
      return;
    }
    try {
      safeSetItem("demo_sorter_results", JSON.stringify(results));
    } catch (e) {
      console.error("Error saving demo results:", e);
    }
  }, [results]);

  const handleDropZoneClick = () => {
    fileInputRef.current.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    handleFilesSelection(files);
  };

  const handleFilesSelection = (files) => {
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImages((prev) => [...prev, { file, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearAll = () => {
    setSelectedImages([]);
    setResults([]);
  };

  const handleProcessImages = async () => {
    try {
      if (selectedImages.length === 0) {
        alert("No images to process. Please upload images first.");
        return;
      }

      setIsProcessing(true);
      setAutoFillMessageIndex(0);

      const form = new FormData();
      selectedImages.forEach((img) => {
        if (img.file) form.append("images", img.file);
      });

      const company_id = localStorage.getItem("company_id") || "demo";
      form.append("company_id", company_id);

      const response = await axios.post(`${API_BASE}/api/autofill/analyze`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const data = response.data;
      const analysis = data.analysis || {};
      const productMetadata = data.product_metadata || {};
      const price = data.price || {};
      const authenticity = data.authenticity || {};
      const condition = data.condition || {};
      const category = analysis.category || {};
      const thriftSuggestion = data.thriftops_suggestion || analysis.thriftops_suggestion || {};

      const rawItem = {
        sku: productMetadata.sku || "",
        title: productMetadata.title || analysis.seo_title || "Unknown Product",
        brand: analysis.brand || "",
        color: analysis.color || "",
        material: analysis.material || "",
        l1_gender: category.l1_gender,
        l2_type: category.l2_type,
        l3_category: category.l3_category,
        l4_subcategory: category.l4_subcategory,
        authenticity_score: authenticity.score ?? analysis.authenticity_score ?? null,
        authenticity_summary: authenticity.summary || analysis.authenticity_summary || "",
        condition_score: condition.score ?? analysis.condition ?? null,
        condition_summary: condition.summary || analysis.condition_summary || "",
        currency: price.currency || "USD",
        price_low: price.low ?? null,
        price_high: price.high ?? null,
        price_recommended: price.recommended ?? null,
        description:
          productMetadata.description || analysis.seo_description || "",
        seo_title: analysis.seo_title || productMetadata.title || "",
        seo_tags: analysis.seo_tags || productMetadata.seo_tags || "",
        sell_channel: thriftSuggestion.channel || thriftSuggestion.sell_channel || "",
        sell_reason: thriftSuggestion.reason || "",
        raw_response: data,
        images: selectedImages.map((img) => img.preview).filter(Boolean),
      };

      const result = {
        image: selectedImages[0].preview,
        fileName: selectedImages[0].file?.name || "Uploaded Image",
        description: rawItem.title,
        price: price.recommended || 0,
        authScore: authenticity.score || 0,
        conditionScore: condition.score || 0,
        suggestion: rawItem.sell_channel || "N/A",
        authReasons: [{ status: "pass", text: authenticity.summary || "No details" }],
        conditionReasons: [{ status: "pass", text: condition.summary || "No details" }],
        raw: rawItem,
      };

      setResults((prev) => [...prev, result]);
      setSelectedImages([]);
      setIsProcessing(false);
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete && onComplete("smartscan");
      }

      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } catch (error) {
      console.error("API ERROR:", error);
      setIsProcessing(false);
      alert("AI API failed. Check console logs.");
    }
  };

  const handleSellOnline = (index) => {
    const result = results[index];
    if (!result || !result.raw) return;

    const item = result.raw;

    const categoryString = [item.l1_gender, item.l2_type, item.l3_category, item.l4_subcategory]
      .filter(Boolean)
      .join(" > ");

    let priceRange = "";
    if (item.price_low != null && item.price_high != null) {
      priceRange = `${item.price_low} - ${item.price_high}`;
    }

    const conditionScore = Number(item.condition_score || 0);
    let condition = "POOR";
    if (conditionScore >= 10) condition = "NEW";
    else if (conditionScore >= 9) condition = "LIKE-NEW";
    else if (conditionScore >= 8) condition = "EXCELLENT";
    else if (conditionScore >= 7) condition = "GOOD";
    else if (conditionScore >= 6) condition = "FAIR";

    let tags = [];
    if (item.seo_tags) {
      tags = item.seo_tags.split(",").map((t) => t.trim()).filter(Boolean);
    }

    const uploadedImages = Array.isArray(item.images) ? item.images : [];
    const blurStatuses = uploadedImages.map(() => false);

    const appState = {
      currentStep: 2,
      formData: {
        uploadedImages,
        categoryAspects: [],
        allCategoryAspects: [],
        categoryId: "",
        blurStatuses,
        imageProcessing: false,
        backgroundRemoval: false,
        blurDetection: false,
        upscaleImage: false,
        title: item.title || "",
        description: item.description || "",
        sku: item.sku || "",
        price: item.price_recommended || "",
        pricerange: priceRange,
        quantity: "1",
        brand: item.brand || "",
        color: item.color || "",
        category: categoryString,
        condition,
        condition_summary: item.condition_summary || "",
        authenticity_score: item.authenticity_score || "",
        authenticity_summary: item.authenticity_summary || "",
        material: item.material || "",
        tags,
        metafields: {},
        variants: [{ id: 1, size: "", color: "", sku: "" }],
        selectedMarketplaces: [],
        poshmarkCategory: "Women",
        poshmarkSubCategory: "",
        poshmarkSizeType: "standard",
        poshmarkAvailability: "available",
        vintedCategory: "",
        vintedCategoryId: "",
        vintedCategoryPath: [],
        vintedPackageSize: "medium",
        vintedSize: "",
      },
    };

    setAddProductState(appState);
    navigate("/products/add");
  };

  const handlePushToRetail = () => {};

  const handleSellWholesale = () => {};

  const openReasonModal = (type, score, reasons, description) => {
    setModalData({ type, score, reasons: reasons || [], description });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalData(null);
  };

  return (
    <>
      {isProcessing && (
        <AutoFillLoader message={autoFillMessages[autoFillMessageIndex]} />
      )}
      <div className="mb-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <span className="badge bg-info text-dark me-2">Trial</span>
          <span className="text-muted small">Results stay in this browser only — nothing is saved to SmartScan DB.</span>
        </div>
        <MDBBtn color="secondary" size="sm" onClick={onBack}>
          Back to demo modules
        </MDBBtn>
      </div>
      <div className="sorter-page">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => handleFilesSelection(Array.from(e.target.files))}
        />
        <div className="sorter-container">
          {selectedImages.length === 0 ? (
            <div className="sorter-options">
              <div
                className="sorter-option-card drop-zone-card"
                onClick={handleDropZoneClick}
                onDragOver={handleDragOver}
                onDrop={handleDragDrop}
              >
                <div className="upload-placeholder">
                  <div className="upload-icon">📷</div>
                  <h3>Drop Images OR Click to Upload</h3>
                  <p>JPG, PNG, WebP • Max 5MB</p>
                </div>
              </div>
            </div>
          ) : null}

          {selectedImages.length > 0 && (
            <div className="image-preview-section">
              <div className="images-grid">
                {selectedImages.map((img, index) => (
                  <div key={index} className="preview-card">
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => handleRemoveImage(index)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                    <img src={img.preview} alt={`Selected ${index + 1}`} className="preview-image" />
                    <div className="preview-info">
                      <p className="file-name">{img.file?.name || `Photo ${index + 1}`}</p>
                      <p className="file-size">{(img.file?.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="preview-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClearAll();
                  }}
                  style={{ touchAction: "manipulation" }}
                >
                  Clear All ({selectedImages.length})
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleProcessImages();
                  }}
                  style={{ touchAction: "manipulation" }}
                >
                  Process {selectedImages.length} Image{selectedImages.length !== 1 ? "s" : ""}
                </button>
              </div>
            </div>
          )}

          {results.length > 0 && (
            <div className="results-section" ref={resultsRef}>
              <h2 className="results-title">Processing Results</h2>
              <div className="result-table-wrapper">
                <table className="result-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Product Description</th>
                      <th>Potential Price</th>
                      <th>Authentication Score</th>
                      <th>Condition Score</th>
                      <th>Thriftops Suggestion</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((result, index) => (
                      <tr key={index} className="result-row">
                        <td className="image-cell" data-label="Image">
                          <img src={result.image} alt={result.fileName} className="result-thumbnail" />
                        </td>
                        <td className="description-cell" data-label="Product Description">
                          <div className="description-text">{result.description}</div>
                          <div className="file-name-small">{result.fileName}</div>
                        </td>
                        <td className="price-cell" data-label="Potential Price">
                          <span className="price-value">${result.price}</span>
                        </td>
                        <td className="score-cell" data-label="Authentication Score">
                          <div className="score-with-icon">
                            <div className="score-badge score-auth">{result.authScore}%</div>
                            <button
                              type="button"
                              className="eye-icon-btn"
                              onClick={() =>
                                openReasonModal(
                                  "Authentication",
                                  result.authScore,
                                  result.authReasons,
                                  result.description
                                )
                              }
                              title="View authentication details"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            </button>
                          </div>
                        </td>
                        <td className="score-cell" data-label="Condition Score">
                          <div className="score-with-icon">
                            <div className="score-badge score-condition">
                              {result.conditionScore}/10
                            </div>
                            <button
                              type="button"
                              className="eye-icon-btn"
                              onClick={() =>
                                openReasonModal(
                                  "Condition",
                                  result.conditionScore,
                                  result.conditionReasons,
                                  result.description
                                )
                              }
                              title="View condition details"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            </button>
                          </div>
                        </td>
                        <td className="suggestion-cell" data-label="Thriftlops Suggestion">
                          <span className="suggestion-text">{result.suggestion}</span>
                        </td>
                        <td className="actions-cell" data-label="Actions">
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="btn-primary btn-sm"
                              onClick={() => handleSellOnline(index)}
                              title="Sell online"
                            >
                              Sell Online
                            </button>
                            <button
                              type="button"
                              className="btn-secondary btn-sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handlePushToRetail(index);
                              }}
                              title="Push to retail (disabled)"
                              disabled
                            >
                              Push to Retail
                            </button>
                            <button
                              type="button"
                              className="btn-secondary btn-sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSellWholesale(index);
                              }}
                              title="Sell in wholesale (disabled)"
                              disabled
                            >
                              Wholesale
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {modalOpen && modalData && (
            <div className="reason-modal-overlay" onClick={closeModal}>
              <div className="reason-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <div className="modal-title-row">
                    <div className="modal-title-section">
                      <h2 className="modal-product-title">{modalData.description}</h2>
                      <h3 className="modal-analysis-type">{modalData.type} Analysis</h3>
                    </div>
                    <div className="modal-score">
                      <span className="score-label">Score</span>
                      <span
                        className={`score-value ${
                          modalData.type === "Authentication" ? "score-auth" : "score-condition"
                        }`}
                      >
                        {modalData.score}/10
                      </span>
                    </div>
                  </div>
                </div>
                <div className="modal-body">
                  <h4 className="reasons-heading">Analysis Details:</h4>
                  <ul className="reasons-list">
                    {modalData.reasons && Array.isArray(modalData.reasons) && modalData.reasons.length > 0 ? (
                      modalData.reasons.map((reason, idx) => (
                        <li key={idx} className={`reason-item reason-${reason.status}`}>
                          <span className="reason-icon">
                            {reason.status === "pass" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                              </svg>
                            ) : reason.status === "warning" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                                <line x1="12" y1="9" x2="12" y2="13"></line>
                                <line x1="12" y1="17" x2="12.01" y2="17"></line>
                              </svg>
                            ) : (
                              ""
                            )}
                          </span>
                          <span className="reason-text">{reason.text}</span>
                        </li>
                      ))
                    ) : (
                      <li className="reason-item reason-pass">
                        <span className="reason-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                          </svg>
                        </span>
                        <span className="reason-text">
                          No detailed analysis available for this item. Please process the images again to generate
                          detailed reasons.
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
                <div className="modal-footer">
                  <button type="button" className="modal-btn-close" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function DemoPage() {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savedData, setSavedData] = useState(null);
  const [features, setFeatures] = useState([]);
  const [activeFeature, setActiveFeature] = useState(null);
  const [completedFeatures, setCompletedFeatures] = useState(() => {
    try {
      const raw = localStorage.getItem(DEMO_PROGRESS_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed?.completed) ? parsed.completed : [];
    } catch (e) {
      return [];
    }
  });
  const [demoPlan, setDemoPlan] = useState("Free");
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);

  const coreDemoIds = useMemo(
    () =>
      features
        .filter((id) => id === "smartlist" || id === "smartscan")
        .filter((id, index, arr) => arr.indexOf(id) === index),
    [features],
  );

  const coreDemosComplete =
    coreDemoIds.length > 0 && coreDemoIds.every((id) => completedFeatures.includes(id));

  const smartScanLocked =
    coreDemoIds.includes("smartscan") &&
    coreDemoIds.includes("smartlist") &&
    !completedFeatures.includes("smartlist");

  useEffect(() => {
    const persistCheckout = async () => {
      const checkout = query.get("checkout");
      const sessionId = query.get("session_id");

      let pendingLocal = {};
      try {
        pendingLocal = JSON.parse(
          localStorage.getItem("pending_demo_checkout") || "{}",
        );
      } catch (e) {
        pendingLocal = {};
      }

      const existingSession = loadDemoSession() || createDefaultDemoSession();
      const localFeatures = Array.isArray(pendingLocal?.addons)
        ? pendingLocal.addons
        : existingSession.selected_features || [];
      const localPlan = pendingLocal?.plan || existingSession.plan || "Free";

      if (checkout !== "success" || !sessionId) {
        const ensured = buildDemoSessionFromCheckout({
          plan: localPlan,
          selected_features: localFeatures.length ? localFeatures : existingSession.selected_features,
          email: pendingLocal?.email || existingSession.email || "",
          name: pendingLocal?.name || existingSession.name || "",
          signup_context:
            pendingLocal?.signup_context || existingSession.signup_context || "",
          checkout_status: checkout || existingSession.checkout_status || "free",
          session_id: existingSession.session_id || "",
          remaining_demo_feature: existingSession.remaining_demo_feature,
          completed_features: existingSession.completed_features,
        });
        setDemoPlan(ensured.plan || "Free");
        setFeatures(
          (ensured.selected_features || []).map(normalizeFeature).filter(Boolean),
        );
        setCompletedFeatures(
          Array.isArray(ensured.completed_features)
            ? ensured.completed_features
            : [],
        );
        setError("");
        setLoading(false);
        return;
      }

      const companyId = localStorage.getItem("company_id");

      const body = {
        session_id: sessionId,
        checkout_status: checkout,
        selected_features: localFeatures,
        selected_plan: localPlan,
        email: pendingLocal?.email || "",
        name: pendingLocal?.name || "",
        signup_context: pendingLocal?.signup_context || "",
        company_id: companyId,
      };

      try {
        const res = await fetch(config.DEMO_CHECKOUT_SAVE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        const planFromApi =
          data?.selected_plan || data?.plan || data?.subscription_plan || "";
        const rawList = resolveFeatureList({
          apiFeatures: data?.selected_features,
          localAddons: localFeatures,
          planFromApi,
          planFromLocal: localPlan,
        });

        const session = buildDemoSessionFromCheckout({
          plan: planFromApi || localPlan,
          selected_features: rawList,
          email: pendingLocal?.email || "",
          name: pendingLocal?.name || "",
          signup_context: pendingLocal?.signup_context || "",
          checkout_status: "success",
          session_id: sessionId,
          remaining_demo_feature: existingSession.remaining_demo_feature,
          completed_features: existingSession.completed_features,
        });

        setSavedData(data);
        setError("");
        setDemoPlan(session.plan || "Free");
        setFeatures(rawList.map(normalizeFeature).filter(Boolean));
        setCompletedFeatures(
          Array.isArray(session.completed_features)
            ? session.completed_features
            : [],
        );

        if (res.ok) {
          localStorage.removeItem("pending_demo_checkout");
        }
      } catch (e) {
        const fallbackRaw = resolveFeatureList({
          apiFeatures: [],
          localAddons: localFeatures,
          planFromApi: "",
          planFromLocal: localPlan,
        });
        const session = buildDemoSessionFromCheckout({
          plan: localPlan,
          selected_features: fallbackRaw,
          email: pendingLocal?.email || "",
          name: pendingLocal?.name || "",
          signup_context: pendingLocal?.signup_context || "",
          checkout_status: "local_fallback",
          session_id: sessionId,
          remaining_demo_feature: existingSession.remaining_demo_feature,
          completed_features: existingSession.completed_features,
        });
        setDemoPlan(session.plan || "Free");
        setFeatures(fallbackRaw.map(normalizeFeature).filter(Boolean));
        setCompletedFeatures(
          Array.isArray(session.completed_features)
            ? session.completed_features
            : [],
        );
        setError("Could not save checkout details to backend. Showing local selection.");
      } finally {
        setLoading(false);
      }
    };

    persistCheckout();
  }, [query]);

  const featureCards = useMemo(() => {
    const unique = features
      .filter((id, index, arr) => arr.indexOf(id) === index)
      .filter((id) => FEATURE_META[id]);
    unique.sort((a, b) => {
      const ia = DEMO_CARD_ORDER.indexOf(a);
      const ib = DEMO_CARD_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    return unique.map((id) => ({ id, ...FEATURE_META[id] }));
  }, [features]);

  useEffect(() => {
    const remaining = {
      smartscan: !completedFeatures.includes("smartscan"),
      smartlist: !completedFeatures.includes("smartlist"),
      pos: !completedFeatures.includes("pos"),
    };
    const existing = loadDemoSession() || createDefaultDemoSession();
    buildDemoSessionFromCheckout({
      ...existing,
      plan: demoPlan || existing.plan || "Free",
      selected_features: features.length
        ? features
        : existing.selected_features,
      email: existing.email || "",
      name: existing.name || "",
      signup_context: existing.signup_context || "",
      checkout_status: existing.checkout_status || "free",
      session_id: existing.session_id || "",
    });
    const merged = loadDemoSession() || existing;
    localStorage.setItem(
      DEMO_SESSION_KEY,
      JSON.stringify({
        ...merged,
        remaining_demo_feature: remaining,
        completed_features: Object.entries(remaining)
          .filter(([, v]) => v === false)
          .map(([id]) => id),
        updated_at: Date.now(),
      }),
    );

    localStorage.setItem(
      DEMO_PROGRESS_STORAGE_KEY,
      JSON.stringify({
        completed: completedFeatures,
        selected: features,
        plan: demoPlan,
        remaining_demo_feature: remaining,
      }),
    );
  }, [completedFeatures, demoPlan, features]);

  useEffect(() => {
    if (loading || activeFeature) return;
    if (!coreDemosComplete) return;
    if (localStorage.getItem(DEMO_INTEGRATION_MODAL_DISMISS_KEY)) return;
    setShowIntegrationModal(true);
  }, [loading, activeFeature, coreDemosComplete]);

  const dismissIntegrationModal = () => {
    localStorage.setItem(DEMO_INTEGRATION_MODAL_DISMISS_KEY, "1");
    setShowIntegrationModal(false);
  };

  const goToMarketplaceSetup = () => {
    localStorage.setItem(DEMO_INTEGRATION_MODAL_DISMISS_KEY, "1");
    setShowIntegrationModal(false);
    navigate("/onboarding/marketplaces");
  };

  const markFeatureCompleted = (featureId) => {
    markDemoFeatureCompleted(featureId);
    setCompletedFeatures((prev) =>
      prev.includes(featureId) ? prev : [...prev, featureId],
    );
  };

  if (!loading && activeFeature === "smartscan") {
    return (
      <MDBContainer fluid className="py-4 px-4" style={{ minHeight: "100vh", background: "#f4f7ff" }}>
        <div className="mx-auto" style={{ maxWidth: "1200px" }}>
          <div className="bg-white rounded-4 border shadow-sm p-3 p-md-4">
            <DemoSmartScanPane
              onBack={() => setActiveFeature(null)}
              onComplete={markFeatureCompleted}
            />
          </div>
        </div>
      </MDBContainer>
    );
  }

  if (!loading && activeFeature === "smartlist") {
    return (
      <MDBContainer fluid className="py-4 px-4" style={{ minHeight: "100vh", background: "#f4f7ff" }}>
        <div className="mx-auto" style={{ maxWidth: "1400px" }}>
          <div className="bg-white rounded-4 border shadow-sm p-2 p-md-3">
            <DemoSmartListPane
              onBack={() => setActiveFeature(null)}
              onComplete={markFeatureCompleted}
            />
          </div>
        </div>
      </MDBContainer>
    );
  }

  return (
    <MDBContainer fluid className="py-5 px-4" style={{ minHeight: "100vh", background: "#f4f7ff" }}>
      <div className="mx-auto" style={{ maxWidth: "1100px" }}>
        <div
          className="p-4 p-md-5 mb-4 rounded-4"
          style={{
            background: "linear-gradient(120deg, #2d5bff 0%, #6f7eff 60%, #9a86ff 100%)",
            color: "#fff",
          }}
        >
          <h2 className="fw-bold mb-2">Welcome to your Demo Space</h2>
          <p className="mb-0">
            You selected a plan. Choose a module below to start a guided demo so you can learn how the app works.
            {smartScanLocked && (
              <span className="d-block mt-2 small" style={{ opacity: 0.95 }}>
                Try Smart List first, then Smart Scan, so the walkthrough matches how teams usually onboard.
              </span>
            )}
          </p>
        </div>

        {!loading && coreDemosComplete && (
          <div className="mb-4 p-3 p-md-4 rounded-4 border bg-white shadow-sm">
            <p className="mb-2 fw-semibold mb-md-0 d-md-inline">
              Hey, do you want to integrate your marketplace and settings?
            </p>{" "}
            <MDBBtn color="primary" size="sm" className="mt-2 mt-md-0" onClick={goToMarketplaceSetup}>
              Click here
            </MDBBtn>
          </div>
        )}

        <MDBModal open={showIntegrationModal} tabIndex="-1" staticBackdrop>
          <MDBModalDialog centered>
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>Connect your marketplaces</MDBModalTitle>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={dismissIntegrationModal}
                />
              </MDBModalHeader>
              <MDBModalBody>
                Hey, do you want to integrate your marketplace and settings? We will walk you through connections and
                key settings step by step. You can skip anything you are not ready for.
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn color="light" onClick={dismissIntegrationModal}>
                  Not now
                </MDBBtn>
                <MDBBtn color="primary" onClick={goToMarketplaceSetup}>
                  Yes, let&apos;s go
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

        {loading && <p className="text-muted">Preparing your demo options...</p>}
        {!loading && error && <p className="text-danger">{error}</p>}

        {!loading && !featureCards.length && (
          <div className="bg-white rounded-4 p-4 border">
            <h5 className="mb-2">No demo features found</h5>
            <p className="mb-0 text-muted">
              We could not detect selected features yet. Please contact support or retry payment flow.
            </p>
          </div>
        )}

        {!loading && featureCards.length > 0 && (
          <div className="row g-4">
            {featureCards.map((feature) => (
              <div className="col-12 col-md-6" key={feature.id}>
                <div className="h-100 bg-white rounded-4 border shadow-sm p-4 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h4 className="fw-bold mb-0">{feature.title}</h4>
                    <span className="badge bg-primary">{feature.badge}</span>
                  </div>
                  <p className="text-muted mb-4">{feature.description}</p>
                  <div className="mt-auto">
                    <MDBBtn
                      color="primary"
                      className="w-100"
                      disabled={smartScanLocked && feature.id === "smartscan"}
                      onClick={() => setActiveFeature(feature.id)}
                    >
                      {smartScanLocked && feature.id === "smartscan"
                        ? "Complete Smart List demo first"
                        : `Start ${feature.title} Demo`}
                    </MDBBtn>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading &&
          activeFeature &&
          activeFeature !== "smartscan" &&
          activeFeature !== "smartlist" &&
          FEATURE_META[activeFeature] && (
          <div className="mt-4 bg-white rounded-4 border shadow-sm p-4">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <h3 className="fw-bold mb-0">{FEATURE_META[activeFeature].title} Guided Demo</h3>
                <p className="text-muted mb-0">
                  This is not “test mode”. It is a live walkthrough to help you understand the feature.
                </p>
              </div>
              <MDBBtn color="link" className="text-decoration-none" onClick={() => setActiveFeature(null)}>
                Back
              </MDBBtn>
            </div>

            <div className="mt-3">
              <div className="d-flex gap-3 flex-wrap">
                <span className="badge bg-success">Step 1: Explore</span>
                <span className="badge bg-success">Step 2: Try sample flow</span>
                <span className="badge bg-success">Step 3: Continue on your dashboard</span>
              </div>

              <p className="mt-3 mb-2 text-muted">
                Demo content will be connected to the real feature pages next. For now, you can proceed and we’ll
                refine this walkthrough UI together.
              </p>

              <MDBBtn
                color="secondary"
                className="w-100"
                onClick={() => {
                  setSavedData((prev) => ({
                    ...(prev || {}),
                    message: "Demo started. Next we will connect this to SmartScan/POS flows.",
                  }));
                }}
              >
                Continue Demo
              </MDBBtn>
            </div>
          </div>
        )}

        {!loading && savedData?.message && (
          <p className="small text-muted mt-3 mb-0">{savedData.message}</p>
        )}
      </div>
    </MDBContainer>
  );
}

function DemoSmartListPane({ onBack, onComplete }) {
  const originalCompanyIdRef = useRef(null);
  const uploadHintShownRef = useRef(false);
  const completedRef = useRef(false);
  const { showInfo, MessageBoxComponent } = useMessageBox();

  useEffect(() => {
    originalCompanyIdRef.current = localStorage.getItem("company_id");
    localStorage.setItem("company_id", "1");

    return () => {
      if (originalCompanyIdRef.current === null) {
        localStorage.removeItem("company_id");
      } else {
        localStorage.setItem("company_id", originalCompanyIdRef.current);
      }
    };
  }, []);

  const handleDemoCapture = (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== "function") return;

    if (
      !uploadHintShownRef.current &&
      target.closest("#image-upload, .upload-area, #file-input")
    ) {
      uploadHintShownRef.current = true;
      showInfo(
        "You are now entering the Smart List guided walkthrough. In this demo, you can practice image upload and Auto Fill exactly as in the live workflow, while your marketplace channels and product database remain fully protected.",
        "Guided Smart List Demo",
      );
    }

    const createButton = target.closest("button");
    if (
      createButton &&
      /create listing/i.test((createButton.textContent || "").trim())
    ) {
      event.preventDefault();
      event.stopPropagation();
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete && onComplete("smartlist");
      }
      showInfo(
        "Your product has been successfully uploaded across all platforms.\n\nShopify listings go live instantly, eBay products are saved as drafts and then automatically published within seconds, and Poshmark/Vinted uploads are handled via browser automation, no action needed from your side.",
        "Upload Successful",
      );
    }
  };

  return (
    <div onClickCapture={handleDemoCapture}>
      <div className="d-flex justify-content-end mb-2">
        <MDBBtn color="secondary" size="sm" onClick={onBack}>
          Back to demo modules
        </MDBBtn>
      </div>
      <AddProduct />
      <MessageBoxComponent />
    </div>
  );
}

export default DemoPage;
